/**
 * Sentry init for the Expo web build (React DOM target at app.tago.rent).
 *
 * @sentry/react-native has no working web transport, so the native entry
 * in sentry.ts short-circuits on web and this module takes over via
 * dynamic import. The web project is selected by the deployment DSN so
 * release cadence + alert rules stay disentangled from the native project.
 *
 * PII minimisation: sendDefaultPii: false. User ID is set via
 * setSentryUser() after signin; email is intentionally omitted. No form
 * inputs are captured by Sentry by default on web, but we defensively
 * disable text capture in Session Replay too. On top of all that,
 * `beforeSend` runs every event through the shared PII scrubber — none of
 * the above stops an email a developer interpolated into an error string.
 */
import * as SentryReact from '@sentry/react'
import { env } from '../env'
import { isExpectedNonFatalOperationalError } from '../errors/service-error'
import { isIsolatedPreviewBuild } from '../runtime-build'
import { normalizeCapturedError } from './sentry-capture'
import { attachSafeEdgeFunctionFailureContext } from './sentry-edge-function-context'
import { scrubSentryEventPII } from './sentry-scrub'

let initialized = false
let initializationAttempted = false

export function initSentryWeb(): void {
  if (initializationAttempted) return
  initializationAttempted = true

  // Preview is a fixtures-only application identity. The web SDK has its own
  // transport (unlike @sentry/react-native), so it needs the same hard stop as
  // the native entry before it reads a DSN or installs BrowserTracing.
  if (isIsolatedPreviewBuild) return

  const dsn = process.env.EXPO_PUBLIC_SENTRY_WEB_DSN ?? env.EXPO_PUBLIC_SENTRY_DSN
  if (!dsn) {
    if (env.isDev) console.warn('[Sentry-web] no DSN — disabled')
    return
  }

  SentryReact.init({
    dsn,
    environment: env.EXPO_PUBLIC_ENV,
    release: typeof window !== 'undefined' && 'location' in window ? undefined : undefined,
    tracesSampleRate: env.isProduction ? 0.2 : 1.0,
    replaysOnErrorSampleRate: env.isProduction ? 0.1 : 0,
    replaysSessionSampleRate: 0,
    sendDefaultPii: false,
    attachStacktrace: true,
    integrations: [
      SentryReact.browserTracingIntegration(),
      SentryReact.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend(event, hint) {
      if (isExpectedNonFatalOperationalError(hint?.originalException, event.level)) {
        return null
      }
      const eventText = JSON.stringify({
        message: event.message,
        exception: event.exception?.values?.map((value) => ({
          type: value.type,
          value: value.value,
        })),
      })
      if (isExpectedNonFatalOperationalError(eventText, event.level)) {
        return null
      }
      // Preserve the bounded diagnostics that FunctionsHttpError otherwise
      // hides behind a generic message. The helper accepts only a public
      // function slug, HTTP status and stable code; it never copies the
      // Response URL, query, headers or body into Sentry.
      attachSafeEdgeFunctionFailureContext(event, hint?.originalException)
      // Strip emails, phone numbers and credential-shaped values out of
      // the message, the exception values, the breadcrumbs and the
      // request payload. Same scrubber the native SDK runs.
      scrubSentryEventPII(event)
      return event
    },
    beforeSendTransaction(event) {
      // BrowserTracing carries request URLs in transaction/span fields.
      // Apply the same last-mile redaction as error events before it leaves
      // the browser, including query strings and fragments.
      scrubSentryEventPII(event)
      return event
    },
    beforeSendSpan(span) {
      // Some BrowserTracing spans are emitted independently of a transaction
      // (for example INP). Route each one through the exact same scrubber so
      // an HTTP URL can never bypass the transaction envelope.
      scrubSentryEventPII({ spans: [span] })
      return span
    },
    beforeBreadcrumb(breadcrumb) {
      // Drop console breadcrumbs in production — they're noisy and can
      // leak app state strings. Payment + auth flows call captureException
      // explicitly when they need trail.
      if (breadcrumb.category === 'console' && env.isProduction) return null
      return breadcrumb
    },
  })

  initialized = true
}

export function setSentryWebUser(user: { id: string; email?: string; username?: string } | null) {
  if (!initialized) return
  if (user) {
    SentryReact.setUser({ id: user.id })
  } else {
    SentryReact.setUser(null)
  }
}

/**
 * Capture an exception with optional context.
 *
 * The context lives on a scope of its own. `SentryReact.setContext` —
 * what this used to call — writes to the global scope, so the payload of
 * the last captured exception stayed attached to every event that came
 * after it, including unrelated ones.
 */
export function captureExceptionWeb(error: unknown, context?: Record<string, unknown>): string {
  if (!initialized) return ''
  if (isExpectedNonFatalOperationalError(error)) {
    SentryReact.addBreadcrumb({
      category: 'operational',
      level: 'warning',
      message: error instanceof Error ? error.message : String(error),
      data: context,
    })
    return ''
  }
  const normalized = normalizeCapturedError(error)
  if (!context) return SentryReact.captureException(normalized)

  return SentryReact.withScope((scope) => {
    scope.setContext('extra', context)
    return SentryReact.captureException(normalized)
  })
}

export function addBreadcrumb(breadcrumb: Parameters<typeof SentryReact.addBreadcrumb>[0]): void {
  if (!initialized) return
  SentryReact.addBreadcrumb(breadcrumb)
}

// ────────────────────────────────────────────────────────────────────────────
// Native API parity exports
//
// `app/_layout.tsx` imports { initSentry, useSentryNavigationTracking,
// setSentryUser, withSentry } from `lib/utils/sentry`. Metro picks `.web.ts`
// over `.ts` on web, so this module IS what _layout sees on the web bundle.
// Without these aliases the imports resolve to `undefined` and the root
// layout crashes with "(0 , S.initSentry) is not a function" — exactly the
// regression that happened in the first prebuilt deploy. Keep these names
// in lockstep with the native sentry.ts API surface.
// ────────────────────────────────────────────────────────────────────────────

/** Native parity: name `initSentry` is what _layout.tsx calls at startup. */
export function initSentry(): void {
  initSentryWeb()
}

/** Native parity: web has no react-navigation Sentry integration; no-op. */
export function useSentryNavigationTracking(): void {
  // Intentionally empty — @sentry/react has no Expo Router native nav hook.
}

/** Native parity: forward the user assignment to the web SDK. */
export function setSentryUser(
  user: { id: string; email?: string; username?: string } | null,
): void {
  setSentryWebUser(user)
}

/** Native parity: `Sentry.wrap` is RN-only; on web pass the component through. */

export function withSentry<T>(component: T): T {
  return component
}

/**
 * Native parity for ad-hoc captures. The web entry already exports
 * `captureExceptionWeb`; this matches the native module's name so callers
 * outside _layout don't have to platform-branch.
 */
export function captureException(error: unknown, context?: Record<string, unknown>): string {
  return captureExceptionWeb(error, context)
}

export const Sentry = SentryReact
